import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { CriteriaGroup } from '@/types/evaluation.types';
import { createDefaultEvaluationTemplate } from '@/services/demo-data/defaultTemplate';
import { generateId } from '@/lib/id';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ArrowLeft, ChevronDown, ChevronUp, FileText, Loader2, Plus } from 'lucide-react';
import { CriteriaGroupCard } from '@/components/criteria/CriteriaGroupCard';
import { CriteriaGroupForm } from '@/components/criteria/CriteriaGroupForm';
import { WeightDistribution } from '@/components/criteria/WeightDistribution';
import { useEvaluation, useSaveEvaluation } from '@/hooks';

export function CriteriaBuilder() {
  const { t } = useTranslation(['criteria', 'common']);
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const { data: evaluation, isLoading } = useEvaluation(id || '');
  const saveEvaluation = useSaveEvaluation();
  const [groupFormOpen, setGroupFormOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<CriteriaGroup | undefined>();
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

  // Initialize expanded groups when evaluation loads
  useEffect(() => {
    if (evaluation) {
      const initialExpanded: Record<string, boolean> = {};
      evaluation.criteriaGroups.forEach((group) => {
        initialExpanded[group.id] = true;
      });
      setExpandedGroups(initialExpanded);
    }
  }, [evaluation]);

  // Redirect if no id or evaluation not found
  useEffect(() => {
    if (!id && !isLoading) {
      navigate('/');
    }
  }, [id, isLoading, navigate]);

  const handleAddGroup = (group: CriteriaGroup) => {
    if (!evaluation) return;

    setSaveStatus('saving');
    const updated = {
      ...evaluation,
      criteriaGroups: [...evaluation.criteriaGroups, group],
    };
    saveEvaluation.mutate(updated, {
      onSuccess: () => {
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus('idle'), 2000);
      },
    });
  };

  const handleUpdateGroup = (updatedGroup: CriteriaGroup) => {
    if (!evaluation) return;

    setSaveStatus('saving');
    const updated = {
      ...evaluation,
      criteriaGroups: evaluation.criteriaGroups.map((g) =>
        g.id === updatedGroup.id ? updatedGroup : g
      ),
    };
    saveEvaluation.mutate(updated, {
      onSuccess: () => {
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus('idle'), 2000);
      },
    });
  };

  const handleDeleteGroup = (groupId: string) => {
    if (!evaluation) return;

    setSaveStatus('saving');
    const updated = {
      ...evaluation,
      criteriaGroups: evaluation.criteriaGroups.filter((g) => g.id !== groupId),
    };
    saveEvaluation.mutate(updated, {
      onSuccess: () => {
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus('idle'), 2000);
      },
    });
  };

  const handleEditGroup = (group: CriteriaGroup) => {
    setEditingGroup(group);
    setGroupFormOpen(true);
  };

  const openAddGroup = () => {
    setEditingGroup(undefined);
    setGroupFormOpen(true);
  };

  const handleLoadTemplate = () => {
    if (!evaluation) return;

    setSaveStatus('saving');
    const template = createDefaultEvaluationTemplate(generateId);
    const updated = {
      ...evaluation,
      criteriaGroups: template.criteriaGroups,
    };
    saveEvaluation.mutate(updated, {
      onSuccess: () => {
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus('idle'), 2000);
      },
    });
  };

  const handleCollapseAll = () => {
    const allCollapsed: Record<string, boolean> = {};
    evaluation?.criteriaGroups.forEach((group) => {
      allCollapsed[group.id] = false;
    });
    setExpandedGroups(allCollapsed);
  };

  const handleExpandAll = () => {
    const allExpanded: Record<string, boolean> = {};
    evaluation?.criteriaGroups.forEach((group) => {
      allExpanded[group.id] = true;
    });
    setExpandedGroups(allExpanded);
  };

  const toggleGroup = (groupId: string) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="sr-only">{t('common:loading')}</span>
        </div>
      </div>
    );
  }

  if (!evaluation) {
    return null;
  }

  //const totalWeight = evaluation.criteriaGroups.reduce((sum, g) => sum + g.weight, 0);
  const hasCriteria = evaluation.criteriaGroups.some((g) => g.criteria.length > 0);

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex-1">
          <Button
            variant="ghost"
            size="sm"
            className="mb-4 -ml-2"
            onClick={() => navigate(`/evaluation/${id}`)}
          >
            <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
            {t('common:navigation.backToDashboard')}
          </Button>
          <h1 className="text-4xl font-bold tracking-tight mb-2">{t('criteria:title')}</h1>
          <p className="text-muted-foreground">{t('criteria:subtitle')}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {saveStatus === 'saving' && (
            <span className="text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              {t('criteria:saving')}
            </span>
          )}
          {saveStatus === 'saved' && (
            <span className="text-sm text-green-600" role="status" aria-live="polite">
              {t('criteria:saved')}
            </span>
          )}
        </div>
      </div>

      {/* Toolbar - Collapse All & Weight Distribution */}
      {evaluation.criteriaGroups.length > 0 && (
        <div className="mb-4 space-y-3">
          {/* Collapse/Expand All Button */}
          <div className="flex justify-end">
            <Button
              variant="ghost"
              size="sm"
              onClick={Object.values(expandedGroups).some((v) => v) ? handleCollapseAll : handleExpandAll}
              className="text-xs h-7"
            >
              {Object.values(expandedGroups).some((v) => v) ? (
                <>
                  <ChevronUp className="h-3 w-3 mr-1.5" aria-hidden="true" />
                  {t('criteria:collapseAll')}
                </>
              ) : (
                <>
                  <ChevronDown className="h-3 w-3 mr-1.5" aria-hidden="true" />
                  {t('criteria:expandAll')}
                </>
              )}
            </Button>
          </div>

          {/* Weight Distribution Bar */}
          <div className="p-3 border rounded-lg bg-card">
            <WeightDistribution groups={evaluation.criteriaGroups} compact />
          </div>
        </div>
      )}

      <div className="space-y-6">
          {/* Empty State */}
          {evaluation.criteriaGroups.length === 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-primary" aria-hidden="true" />
                  {t('criteria:emptyState.title')}
                </CardTitle>
                <CardDescription>{t('criteria:emptyState.description')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button onClick={openAddGroup} className="flex-1">
                    <Plus className="h-4 w-4 mr-2" aria-hidden="true" />
                    {t('criteria:emptyState.createGroup')}
                  </Button>
                  <Button onClick={handleLoadTemplate} variant="outline" className="flex-1">
                    <FileText className="h-4 w-4 mr-2" aria-hidden="true" />
                    {t('criteria:emptyState.loadTemplate')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Criteria Groups */}
          {evaluation.criteriaGroups.length > 0 && (
            <>
              {evaluation.criteriaGroups.map((group) => (
                <CriteriaGroupCard
                  key={group.id}
                  group={group}
                  isExpanded={expandedGroups[group.id] ?? true}
                  onToggleExpand={() => toggleGroup(group.id)}
                  onUpdateGroup={handleUpdateGroup}
                  onDeleteGroup={() => handleDeleteGroup(group.id)}
                  onEditGroup={() => handleEditGroup(group)}
                />
              ))}

              {/* Add Group Button */}
              <Button onClick={openAddGroup} variant="outline" className="w-full">
                <Plus className="h-4 w-4 mr-2" aria-hidden="true" />
                {t('criteria:addGroup')}
              </Button>
            </>
          )}

        {/* Validation Warnings - Inline */}
        {evaluation.criteriaGroups.length > 0 && !hasCriteria && (
          <Alert variant="warning">
            <AlertDescription>
              {t('criteria:warnings.noCriteria')}
            </AlertDescription>
          </Alert>
        )}
      </div>

      {/* Group Form Dialog */}
      <CriteriaGroupForm
        open={groupFormOpen}
        onOpenChange={setGroupFormOpen}
        onSave={editingGroup ? handleUpdateGroup : handleAddGroup}
        group={editingGroup}
        mode={editingGroup ? 'edit' : 'add'}
        existingGroups={evaluation.criteriaGroups}
      />
    </div>
  );
}
